<template>
   <section
      v-if="accountStore.userStudiesResp.length || accountStore.legacyStudyIds.length"
      id="documents"
      class="mx-auto"
      data-cy="user-studies"
   >
      <!-- Standard study card with withdrawal button, description, dates and documents -->
      <UserStudyCard
         v-for="study in accountStore.userStudiesResp"
         :key="study.studyId"
         :study="study"
         :docs="accountStore.docsByStudyId[study.studyId] || []"
         @start-withdrawal="onWithdrawClick"
      />

      <!-- ℹ️ Legacy study card
      Sano's old studies didn't have a concept of participation. However, users could still have documents associated with them.
      This card consists only of a study title (read from the first document) and a documents table. -->
      <UserLegacyStudyCard
         v-for="study in accountStore.legacyStudyIds"
         :key="study"
         :studyId="study"
         :docs="accountStore.docsByStudyId[study]"
      />

      <UserStudyWithdrawalModals
         v-if="accountStore.userStudiesResp.length"
         :step="withdrawalStep"
         :studyId="withdrawingStudyId"
         :studyName="withdrawingStudyName"
         @on-withdrawal-close="onWithdrawalClose"
         @on-next-step="onNextStep"
         @on-previous-step="onPreviousStep"
      />
      <div class="mt-6 flex max-w-80ch text-base leading-tight">
         <svg
            aria-hidden="true"
            focusable="false"
            width="12"
            height="12"
            class="mr-1 mt-1 shrink-0"
            viewBox="0 0 12 12"
            fill="#4C55AA"
         >
            <use xlink:href="#info" />
         </svg>
         <p>
            <span>{{ $t("account.other_data") }}</span
            ><span>&nbsp;</span>
            <button v-if="intercom.isActive" class="inline-block cursor-pointer border-b" @click="intercom.open()">
               {{ $t("account.get_in_touch") }}
            </button>
            <a
               v-else-if="organisationInfo?.email"
               class="inline-block cursor-pointer border-b"
               :href="`mailto:${organisationInfo.email}`"
               >{{ $t("account.get_in_touch") }}</a
            >
            <span v-else>{{ $t("account.get_in_touch") }}</span>
            <span>&nbsp;</span><span> {{ $t("account.with_our_team") }}</span>
         </p>
      </div>
   </section>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import { intercomKey } from "@/plugins/useIntercom";
import { useBaseContentStore } from "@/stores/baseContentStore";
import { useAccountStore } from "@/stores/accountStore";

export type WithdrawalStep = 0 | 1 | 2 | 3;
// [0] all modals closed
// [1] step 1: 'Are you sure?'' modal open
// [2] step 2: 'Withdrawal reason' modal open
// [3] step 3: 'Successfuly withdrawn' modal open

const $t = useI18n().t;
const intercom = inject(intercomKey);
const baseContentStore = useBaseContentStore();
const accountStore = useAccountStore();

const withdrawingStudyId = ref<string>("");
const withdrawingStudyName = ref<string>("");
const withdrawalStep = ref<WithdrawalStep>(0);

// computed
// -----------------------------------------
const organisationInfo = computed(() => {
   if (!baseContentStore.content) return null;

   return baseContentStore.content.organisationInfo;
});

// methods
// -----------------------------------------
// Withdrawal modal steps control panel
// It lives here, so we can render only one UserStudyWithdrawalModals component for all studies
function onWithdrawClick(studyId: string, studyName: string) {
   // Assign studyId and name to refs and open the first modal
   withdrawingStudyId.value = studyId;
   withdrawingStudyName.value = studyName;
   withdrawalStep.value = 1;
}

function onWithdrawalClose() {
   // Reset study ref and close all modals
   withdrawingStudyId.value = "";
   withdrawingStudyName.value = "";
   withdrawalStep.value = 0;
}

function onNextStep() {
   if (withdrawalStep.value === 1) {
      withdrawalStep.value += 1;
   } else if (withdrawalStep.value === 2) {
      // Update study status to 'withdrawn' in store
      accountStore.userStudiesResp.forEach((study) => {
         if (study.studyId === withdrawingStudyId.value) {
            study.status = "withdrawn";
            study.withdrawnAt = new Date().toISOString();
         }
      });
      withdrawalStep.value += 1;
   } else if (withdrawalStep.value === 3) {
      onWithdrawalClose();
   }
}

function onPreviousStep() {
   // Move to previous step
   withdrawalStep.value += -1;
}
</script>
